import { createContext } from "react";
import type { CogoElement } from "../core/types";

/**
 * True while rendering beneath a parent-activation composite host whose own
 * pointer events are disabled (source-derived button hosts set
 * `pointerEvents: "none"` on the host). Raised descendant controls read this
 * to opt into an inline `pointer-events: auto` escape so they stay clickable
 * even when the host utility classes below are not compiled (jsdom, partial
 * CSS builds). Standalone controls must NOT carry the inline escape — the
 * source-derived contract asserts an empty inline pointer-events there.
 */
export const ParentActivationHostContext = createContext(false);

/**
 * Keep real descendant controls above the transparent parent-activation proxy.
 * The proxy remains the hit target for the rest of the parent surface.
 */
export const PARENT_ACTIVATION_HOST_CLASSES = [
  "[&_a[href]]:relative",
  "[&_a[href]]:z-[1]",
  "[&_a[href]]:pointer-events-auto",
  "[&_area[href]]:relative",
  "[&_area[href]]:z-[1]",
  "[&_audio[controls]]:relative",
  "[&_audio[controls]]:z-[1]",
  "[&_button]:relative",
  "[&_button]:z-[1]",
  "[&_button]:pointer-events-auto",
  "[&_embed]:relative",
  "[&_embed]:z-[1]",
  "[&_iframe]:relative",
  "[&_iframe]:z-[1]",
  "[&_input]:relative",
  "[&_input]:z-[1]",
  "[&_input]:pointer-events-auto",
  "[&_label]:relative",
  "[&_label]:z-[1]",
  "[&_object]:relative",
  "[&_object]:z-[1]",
  "[&_select]:relative",
  "[&_select]:z-[1]",
  "[&_select]:pointer-events-auto",
  "[&_summary]:relative",
  "[&_summary]:z-[1]",
  "[&_textarea]:relative",
  "[&_textarea]:z-[1]",
  "[&_textarea]:pointer-events-auto",
  "[&_video[controls]]:relative",
  "[&_video[controls]]:z-[1]",
  "[&_[role=button]]:relative",
  "[&_[role=button]]:z-[1]",
  "[&_[role=button]]:pointer-events-auto",
  "[&_[role=checkbox]]:relative",
  "[&_[role=checkbox]]:z-[1]",
  "[&_[role=combobox]]:relative",
  "[&_[role=combobox]]:z-[1]",
  "[&_[role=link]]:relative",
  "[&_[role=link]]:z-[1]",
  "[&_[role=menuitem]]:relative",
  "[&_[role=menuitem]]:z-[1]",
  "[&_[role=menuitemcheckbox]]:relative",
  "[&_[role=menuitemcheckbox]]:z-[1]",
  "[&_[role=menuitemradio]]:relative",
  "[&_[role=menuitemradio]]:z-[1]",
  "[&_[role=option]]:relative",
  "[&_[role=option]]:z-[1]",
  "[&_[role=radio]]:relative",
  "[&_[role=radio]]:z-[1]",
  "[&_[role=scrollbar]]:relative",
  "[&_[role=scrollbar]]:z-[1]",
  "[&_[role=searchbox]]:relative",
  "[&_[role=searchbox]]:z-[1]",
  "[&_[role=slider]]:relative",
  "[&_[role=slider]]:z-[1]",
  "[&_[role=spinbutton]]:relative",
  "[&_[role=spinbutton]]:z-[1]",
  "[&_[role=switch]]:relative",
  "[&_[role=switch]]:z-[1]",
  "[&_[role=tab]]:relative",
  "[&_[role=tab]]:z-[1]",
  "[&_[role=textbox]]:relative",
  "[&_[role=textbox]]:z-[1]",
  "[&_[role=treeitem]]:relative",
  "[&_[role=treeitem]]:z-[1]",
  "[&_[tabindex]:not([tabindex='-1'])]:relative",
  "[&_[tabindex]:not([tabindex='-1'])]:z-[1]",
  "[&_[contenteditable]:not([contenteditable='false'])]:relative",
  "[&_[contenteditable]:not([contenteditable='false'])]:z-[1]",
  "[&_[draggable=true]]:relative",
  "[&_[draggable=true]]:z-[1]",
  "[&_[data-cogo-composite-action-host]]:relative",
  "[&_[data-cogo-composite-action-host]]:z-[1]",
  "[&_[data-cogo-composite-action-host]]:pointer-events-auto",
].join(" ");

const UNSAFE_PARENT_ACTIVATION_HOST_TAGS = new Set([
  "a",
  "area",
  "audio",
  "button",
  "details",
  "embed",
  "iframe",
  "input",
  "label",
  "object",
  "option",
  "select",
  "summary",
  "textarea",
  "video",
]);

/**
 * Element types that never render their own interactive control surface.
 * Source-derived hosts use this to decide between the plain source-element
 * contract (`data-variant="source"` on a real <a>/<button>/<div role="button">
 * carrying the source inline styles) and the composite host + background-proxy
 * contract that keeps nested controls clickable.
 */
const PASSIVE_SOURCE_DESCENDANT_TYPES = new Set([
  "text",
  "heading",
  "paragraph",
  "label",
  "span",
  "icon",
  "image",
  "shape",
  "badge",
  "avatar",
  "separator",
  "divider",
  "skeleton",
  "kbd",
  "loader",
  "progress",
  "lottie",
]);

/** Structural wrappers — passive themselves; interactivity comes from descendants. */
const STRUCTURAL_SOURCE_DESCENDANT_TYPES = new Set([
  "container",
  "card",
  "section",
  "header",
  "footer",
  "nav",
  "main",
  "aside",
  "article",
  "list",
  "item",
  "item-group",
  "slot",
]);

/**
 * True when any descendant would render its own interactive control: it owns a
 * truthy event binding (inline `events` or an external events.cogo.json tap
 * binding), carries an href, or is a control-ish element type (anything that is
 * neither a known passive leaf nor a structural wrapper). Unknown types count
 * as interactive so exotic subtrees keep the safer composite-host rendering.
 */
export function subtreeContainsInteractiveControl(
  children: CogoElement[] | undefined,
  hasEventBinding?: (elementId: string, eventType: string) => boolean,
): boolean {
  for (const child of children ?? []) {
    if (!child) continue;
    const raw = child as unknown as {
      events?: Record<string, unknown>;
      href?: unknown;
    };
    const ownsEvent =
      raw.events != null &&
      typeof raw.events === "object" &&
      Object.values(raw.events).some(Boolean);
    if (ownsEvent) return true;
    if (child.id && hasEventBinding?.(child.id, "tap")) return true;
    if (raw.href != null) return true;
    const passive =
      PASSIVE_SOURCE_DESCENDANT_TYPES.has(child.type) ||
      STRUCTURAL_SOURCE_DESCENDANT_TYPES.has(child.type);
    if (!passive) return true;
    if (subtreeContainsInteractiveControl(child.children, hasEventBinding)) {
      return true;
    }
  }
  return false;
}

/**
 * A parent activation proxy is itself a native button or link. Never place it
 * inside another interactive host because that creates invalid nested controls
 * and duplicate accessibility nodes.
 */
export function normalizeParentActivationHostTag(
  tag: React.ElementType,
): React.ElementType {
  return typeof tag === "string" &&
    UNSAFE_PARENT_ACTIVATION_HOST_TAGS.has(tag.toLowerCase())
    ? "div"
    : tag;
}

export function parentActivationHostStyle(
  element: CogoElement,
  style?: React.CSSProperties,
): React.CSSProperties {
  const classHasPosition =
    /(?:^|\s)(?:relative|absolute|fixed|sticky)(?:\s|$)/.test(
      element.className ?? "",
    );
  const hasPosition =
    (style?.position != null && style.position !== "static") ||
    element.position?.type != null ||
    classHasPosition;

  return {
    ...style,
    isolation: "isolate",
    minWidth: style?.minWidth ?? "44px",
    minHeight: style?.minHeight ?? "44px",
    ...(hasPosition ? {} : { position: "relative" as const }),
  };
}

export function resolveParentActivationLabel(
  element: CogoElement,
  resolveSymbol: (expression: string) => unknown,
): string {
  const explicit = resolveText(element.ariaLabel, resolveSymbol);
  if (explicit) return explicit;

  const collected = collectElementText(element, resolveSymbol)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
  return collected || element.id;
}

export function ParentActivationProxy({
  elementId,
  href,
  label,
  onClick,
}: {
  elementId: string;
  href?: string;
  label: string;
  onClick: (event: React.MouseEvent) => void;
}) {
  const sharedProps = {
    "aria-label": label,
    "data-cogo-composite-action-proxy": "true",
    "data-cogo-parent-activation-proxy": elementId,
    onClick: (event: React.MouseEvent) => {
      event.stopPropagation();
      onClick(event);
    },
    style: {
      position: "absolute" as const,
      inset: 0,
      // Sit above passive descendants so the whole parent surface is clickable.
      // Interactive descendants are positioned at the same layer and rendered
      // later, so they remain the pointer target instead of the parent proxy.
      zIndex: 1,
      cursor: "pointer",
      border: 0,
      padding: 0,
      background: "transparent",
      color: "transparent",
    },
  };

  if (href) {
    return <a href={href} {...sharedProps} />;
  }

  return <button type="button" {...sharedProps} />;
}

function collectElementText(
  element: CogoElement,
  resolveSymbol: (expression: string) => unknown,
): string[] {
  const options = element.options as Record<string, unknown> | undefined;
  const ownText = resolveText(element.text ?? options?.text, resolveSymbol);
  const ownLabel = resolveText(element.ariaLabel, resolveSymbol);
  const children =
    element.children?.flatMap((child) =>
      collectElementText(child, resolveSymbol),
    ) ?? [];
  return [ownText, ownLabel, ...children].filter(Boolean);
}

function resolveText(
  value: unknown,
  resolveSymbol: (expression: string) => unknown,
): string {
  if (value == null) return "";
  const raw = String(value);
  const resolved = resolveSymbol(raw);
  return resolved == null ? "" : String(resolved).trim();
}
